import {
  designTokens,
  Divider,
  Heading,
  Loader,
  YokuaruBody,
  YokuaruHeader
} from '@itandi/itandi-bb-ui';
import { useState } from 'react';
import { hasV3Header } from '@itandi/brand-ui/ItandiHeader';
import dayjs from 'dayjs';
import { DefaultV2 } from '~/components/common/layouts/DefaultV2';
import {
  dealApplicationStatusQueryParam,
  dealStatusQueryParam
} from '~/models/DealDashboard';
import { useHomes } from '~/hooks/useHomes';
import { DealTypeFilter } from '../common/home/DealTypeFilter';
import type { DealType } from '~/models/Deal';
import type { StatusCardItems } from '../common/home/StatusCardList';
import { StatusCardList } from '../common/home/StatusCardList';
import useItandiAccount from '~/hooks/useItandiAccount';
import { unassignedStaff } from '~/models/CompanyDealTodo';

export const Page: React.FC = () => {
  const { data: currentAccount } = useItandiAccount().currentAccount();
  const today = dayjs();
  const formatToday = today.format('YYYY-MM-DD');
  const twoDaysLater = today.add(2, 'day').format('YYYY-MM-DD');
  const [dealTypes, setDealTypes] = useState<DealType[]>([
    'restoration',
    'repair',
    'other'
  ]);
  const { data: dealDashboard } = useHomes({ dealTypes });
  if (dealDashboard === null) return <Loader />;
  const dealTypeCards = [
    {
      count: dealDashboard.dealTypeInfo.restorationCount,
      href: '/deals?deal_types[]=restoration',
      label: '原状回復',
      variant: 'default'
    },
    {
      count: dealDashboard.dealTypeInfo.repairCount,
      href: '/deals?deal_types[]=repair',
      label: '修繕',
      variant: 'default'
    },
    {
      count: dealDashboard.dealTypeInfo.otherCount,
      href: '/deals?deal_types[]=other',
      label: 'その他',
      variant: 'default'
    }
  ] as const satisfies StatusCardItems;

  const dealStatusCards = [
    {
      count: dealDashboard.dealStatusInfo.nowAcceptingCount,
      href: `/deals?${dealStatusQueryParam(['10'], dealTypes)}`,
      label: '業者選択中',
      variant: 'default'
    },
    {
      count: dealDashboard.dealStatusInfo.inEstimationCount,
      href: `/deals?${dealStatusQueryParam(['15', '101'], dealTypes)}`,
      label: '見積作成中',
      variant: 'default'
    },
    {
      count: dealDashboard.dealStatusInfo.sendEstimateCount,
      href: `/deals?${dealStatusQueryParam(['40', '105'], dealTypes)}`,
      label: '見積確認中',
      variant: 'default'
    },
    {
      count: dealDashboard.dealStatusInfo.inConstructionCount,
      href: `/deals?${dealStatusQueryParam(['45', '111'], dealTypes)}`,
      label: '工事完了待ち',
      variant: 'default'
    },
    {
      count: dealDashboard.dealStatusInfo.awaitingAcceptanceCount,
      href: `/deals?${dealStatusQueryParam(['90', '190'], dealTypes)}`,
      label: '検収中',
      variant: 'default'
    },
    {
      count: dealDashboard.dealStatusInfo.finishedCount,
      href: `/deals?${dealStatusQueryParam(['95', '100', '200'], dealTypes)}`,
      label: '完了',
      variant: 'default'
    }
  ] as const satisfies StatusCardItems;

  const dealApplicationStatusCards = [
    {
      count: dealDashboard.dealApplicationTypeInfo.requestedCount,
      href: `/deals?${dealApplicationStatusQueryParam(
        'requestEstimation',
        dealTypes
      )}`,
      label: '見積申請あり',
      variant: 'default'
    },
    {
      count: dealDashboard.dealApplicationTypeInfo.requestCount,
      href: `/deals?${dealApplicationStatusQueryParam(
        'requestEstimationByCompany',
        dealTypes
      )}`,
      label: '見積申請中',
      variant: 'default'
    },
    {
      count: dealDashboard.dealApplicationTypeInfo.draftCount,
      href: `/deals?${dealApplicationStatusQueryParam(
        'draftEstimationByCompany',
        dealTypes
      )}`,
      label: '見積編集中',
      variant: 'default'
    },
    {
      count: dealDashboard.dealApplicationTypeInfo.waitingCancelCount,
      href: `/deals?${dealApplicationStatusQueryParam('cancel', dealTypes)}`,
      label: '取下申請',
      variant: 'default'
    },
    {
      count: dealDashboard.dealApplicationTypeInfo.acceptedCancelCount,
      href: `/deals?${dealStatusQueryParam(['901'], dealTypes)}`,
      label: '取下完了',
      variant: 'default'
    }
  ] as const satisfies StatusCardItems;

  const dealTodoCards = [
    {
      count: dealDashboard.dealTodoInfo.dueOverCount,
      href: `/todos?completed=false&dueDateTo=${formatToday}`,
      label: '期限切れ',
      variant: 'past'
    },
    {
      count: dealDashboard.dealTodoInfo.dueTodayCount,
      href: `/todos?completed=false&dueDateFrom=${formatToday}&dueDateTo=${formatToday}`,
      label: '本日中期限',
      variant: 'today'
    },
    {
      count: dealDashboard.dealTodoInfo.dueInThreeDaysCount,
      href: `/todos?completed=false&dueDateFrom=${formatToday}&dueDateTo=${twoDaysLater}`,
      label: '期限3日以内',
      variant: 'default'
    },
    {
      count: dealDashboard.dealTodoInfo.incompleteCount,
      href: `/todos?completed=false`,
      label: '未完了',
      variant: 'default'
    },
    {
      count: dealDashboard.dealTodoInfo.unassignedCount,
      href: `/todos?staffId=${unassignedStaff.value}&completed=false`,
      label: '担当者未設定',
      variant: 'default'
    }
  ] as const satisfies StatusCardItems;

  if (currentAccount === null) return null;

  return (
    <DefaultV2>
      <YokuaruHeader
        heading={
          <Heading as="h1">
            {hasV3Header(currentAccount.productParams)
              ? 'ダッシュボード'
              : 'HOME'}
          </Heading>
        }
      />
      <YokuaruBody>
        <div>
          <Heading as="h2" mb={designTokens['spacing/m']}>
            進行中の案件
          </Heading>
          <StatusCardList statusCards={dealTypeCards} />
        </div>
        <Divider color={designTokens['color/border/default']} />
        <DealTypeFilter
          dealDashboard={dealDashboard}
          dealTypes={dealTypes}
          setDealTypes={setDealTypes}
        />
        <div>
          <Heading as="h3" color="subtle" mb={designTokens['spacing/m']}>
            ステータス状況
          </Heading>
          <StatusCardList statusCards={dealStatusCards} />
        </div>
        <div>
          <Heading as="h3" color="subtle" mb={designTokens['spacing/m']}>
            申請状況
          </Heading>
          <StatusCardList statusCards={dealApplicationStatusCards} />
        </div>
        <Divider color={designTokens['color/border/default']} />
        <div>
          <Heading as="h2" mb={designTokens['spacing/m']}>
            TODO状況
          </Heading>
          <StatusCardList statusCards={dealTodoCards} />
        </div>
      </YokuaruBody>
    </DefaultV2>
  );
};
